import { clsx } from "clsx";
import React, { forwardRef } from "react";

import { twMerge } from "@/design-system/twMerge";

import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";
import { ConditionalTooltip } from "../Tooltip/Tooltip";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "outline"
  | "ghost"
  | "link"
  | "destructive"
  | "link-destructive"
  | "icon-primary"
  | "icon-secondary"
  | "icon-outline"
  | "icon-ghost";

export type ButtonSize = "xs" | "sm" | "md" | "lg";

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  className?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  tooltipContent?: React.ReactNode;
  loading?: boolean;
  /** Shown next to the spinner while loading. Without it, only the spinner shows. */
  loadingLabel?: React.ReactNode;
};

const baseClasses =
  "border-box inline-flex items-center justify-center rounded-lg whitespace-nowrap outline-focus-ring transition-all not-disabled:cursor-pointer focus:outline-2 disabled:bg-surface-disabled disabled:text-textDisabled disabled:border-borderDisabled";

const iconOnlyVariants = new Set<ButtonVariant>([
  "icon-primary",
  "icon-secondary",
  "icon-outline",
  "icon-ghost",
]);

type SizeTokens = {
  rect: string;
  rectLeading: string;
  rectTrailing: string;
  rectBoth: string;
  icon: string;
  spinner: string;
  iconSize: string;
  gap: string;
};

const sizeStyles: Record<ButtonSize, SizeTokens> = {
  xs: {
    rect: "h-8 px-2 py-2 text-label-xs",
    rectLeading: "h-8 px-2 py-2 text-label-xs",
    rectTrailing: "h-8 px-2 py-2 text-label-xs",
    rectBoth: "h-8 px-2 py-2 text-label-xs",
    icon: "size-8",
    spinner: "size-3",
    iconSize: "size-icon-xs",
    gap: "gap-1",
  },
  sm: {
    rect: "h-9 px-3 py-2.5 text-label-xs",
    rectLeading: "h-9 pl-2.5 pr-3 py-2.5 text-label-xs",
    rectTrailing: "h-9 pl-3 pr-2.5 py-2.5 text-label-xs",
    rectBoth: "h-9 px-2.5 py-2.5 text-label-xs",
    icon: "size-9",
    spinner: "size-3.5",
    iconSize: "size-icon-sm",
    gap: "gap-1",
  },
  md: {
    rect: "h-10 px-4 py-2.5 text-label-sm",
    rectLeading: "h-10 pl-3 pr-4 py-2.5 text-label-sm",
    rectTrailing: "h-10 pl-4 pr-3 py-2.5 text-label-sm",
    rectBoth: "h-10 px-3 py-2.5 text-label-sm",
    icon: "size-10",
    spinner: "size-4",
    iconSize: "size-icon-md",
    gap: "gap-1.5",
  },
  lg: {
    rect: "h-11 px-5 py-3 text-label-md",
    rectLeading: "h-11 pl-3.5 pr-5 py-3 text-label-md",
    rectTrailing: "h-11 pl-5 pr-3.5 py-3 text-label-md",
    rectBoth: "h-11 px-3.5 py-3 text-label-md",
    icon: "size-11",
    spinner: "size-icon-lg",
    iconSize: "size-icon-lg",
    gap: "gap-1.5",
  },
};

// Apply a default size class to an icon element while letting the caller
// override by passing their own h-/w- class. twMerge resolves conflicts in
// favor of the caller's class. Non-element values (e.g. strings, null) are
// returned as-is.
const withDefaultIconSize = (
  icon: React.ReactNode,
  defaultClass: string,
): React.ReactNode => {
  if (!React.isValidElement(icon)) return icon;
  const element = icon as React.ReactElement<{ className?: string }>;
  return React.cloneElement(element, {
    className: twMerge(defaultClass, element.props.className),
  });
};

const filledPrimary =
  "bg-action-primary text-action-primary-text not-disabled:hover:bg-action-primary-hover not-disabled:active:bg-action-primary-active";
const filledSecondary =
  "bg-action-secondary text-action-secondary-text not-disabled:hover:bg-action-secondary-hover not-disabled:active:bg-action-secondary-active";
const outlined =
  // Fill is intentionally transparent, not a surface token — outline buttons
  // should match whatever canvas/surface they're placed on (page, card,
  // dialog, ...) rather than impose their own background.
  // Hover/active fills match ghostNeutral's exactly.
  "bg-transparent text-textDefault border border-borderDefault not-disabled:hover:bg-surface-hover not-disabled:active:bg-surface-pressed disabled:bg-transparent";
const ghostNeutral =
  "bg-transparent text-textDefault not-disabled:hover:bg-surface-hover not-disabled:active:bg-surface-pressed disabled:bg-transparent";

const variantStyles: Record<ButtonVariant, string> = {
  primary: filledPrimary,
  secondary: filledSecondary,
  outline: outlined,
  ghost: ghostNeutral,
  // Color stays constant across default/hover/active; underline alone
  // signals interactivity. A numbered-shade escalation (e.g. blue-500 to
  // blue-700) gets progressively darker, which reads fine on a light
  // background but loses all contrast once the same shades sit on dark.
  link: "bg-transparent text-link px-0 not-disabled:hover:underline disabled:bg-transparent",
  destructive:
    "bg-red-600 text-white not-disabled:hover:bg-red-700 not-disabled:active:bg-red-800",
  "link-destructive":
    "bg-transparent text-link-destructive px-0 not-disabled:hover:underline disabled:bg-transparent",
  "icon-primary": filledPrimary,
  "icon-secondary": filledSecondary,
  "icon-outline": outlined,
  "icon-ghost": ghostNeutral,
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      leadingIcon,
      trailingIcon,
      variant = "primary",
      size = "md",
      children,
      type = "button",
      tooltipContent,
      loading,
      loadingLabel,
      ...rest
    },
    ref,
  ) => {
    const isIconOnly = iconOnlyVariants.has(variant);
    const sizes = sizeStyles[size];

    const hasLeading = !!leadingIcon || (!!loading && !!loadingLabel);
    const hasTrailing = !!trailingIcon;
    const rectClass = isIconOnly
      ? `shrink-0 p-0 ${sizes.icon}`
      : hasLeading && hasTrailing
        ? sizes.rectBoth
        : hasLeading
          ? sizes.rectLeading
          : hasTrailing
            ? sizes.rectTrailing
            : sizes.rect;

    const buttonItself = (
      <button
        ref={ref}
        {...rest}
        disabled={rest.disabled || loading}
        type={type}
        className={twMerge(
          clsx(baseClasses, sizes.gap, rectClass, variantStyles[variant]),
          className,
          rest.disabled && "cursor-not-allowed",
        )}
      >
        {loading ? (
          <>
            <LoadingSpinner className={sizes.spinner} />
            {!isIconOnly && loadingLabel}
          </>
        ) : isIconOnly ? (
          withDefaultIconSize(children, sizes.iconSize)
        ) : (
          <>
            {withDefaultIconSize(leadingIcon, sizes.iconSize)}
            {children}
            {withDefaultIconSize(trailingIcon, sizes.iconSize)}
          </>
        )}
      </button>
    );

    return (
      <ConditionalTooltip condition={!!tooltipContent} content={tooltipContent}>
        {buttonItself}
      </ConditionalTooltip>
    );
  },
);

Button.displayName = "Button";
